import { useEffect, useState } from 'react';
import type { AnimationEvent } from 'react';

/**
 * Motion is off under test: an element that is still arriving is an element
 * the click lands beside. Cypress announces itself on window, jest sets
 * NODE_ENV. App.tsx pairs this with antd's `motion` seed token and the
 * `[data-motion='off']` rule in motion.css.
 */
export const motionEnabled = !(
    process.env.NODE_ENV === 'test' ||
    (typeof window !== 'undefined' && (window as any).Cypress)
);

/* Long enough to outlast any exit in the token scale, short enough that a
   panel which never animates is not left on screen. */
const EXIT_FALLBACK_MS = 400;

/**
 * Keeps a conditionally-rendered element mounted long enough to animate out.
 *
 * `{open && <Panel />}` unmounts the moment it closes, so there is nothing
 * left to animate and the panel vanishes. This holds it for the length of its
 * exit animation and drops it after — which keeps the mount-on-open behaviour
 * the lazy-loading panels rely on, unlike a CSS-only collapse that would need
 * the panel mounted at zero height from the start.
 *
 *     const { mounted, motionProps } = usePresence(open);
 *     return mounted && <div className="nx-collapse" {...motionProps}>…</div>;
 *
 * The element must carry an exit animation for `data-state='closing'` — see
 * `.nx-collapse` in motion.css — or it sits out the fallback before leaving.
 */
export const usePresence = (open: boolean) => {
    const [mounted, setMounted] = useState(open);

    useEffect(() => {
        if (open) setMounted(true);
        // With motion off there is no exit to wait for.
        else if (!motionEnabled) setMounted(false);
    }, [open]);

    // Backstop for an element whose animationend never arrives — one that is
    // display:none by the time it closes, or a browser that dropped the frame.
    useEffect(() => {
        if (open || !mounted || !motionEnabled) return undefined;
        const timer = setTimeout(() => setMounted(false), EXIT_FALLBACK_MS);
        return () => clearTimeout(timer);
    }, [open, mounted]);

    const onAnimationEnd = (event: AnimationEvent<HTMLElement>) => {
        // a child finishing its own animation is not this one closing
        if (event.target !== event.currentTarget) return;
        if (!open) setMounted(false);
    };

    return {
        mounted,
        motionProps: {
            'data-state': open ? 'open' : ('closing' as const),
            onAnimationEnd,
        },
    };
};
